import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort } from '@fortawesome/free-solid-svg-icons';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import '../../../../src/AssetStatusForm.scss'
import './table-sort-icon.scss';
//import Sparklines from './Sparklines';
import { SparklineChart } from '../SparkLine/Sparkline';
import Sparklines from './Sparklines';
import {
    fetchEventLogDetails,
    getHoldStateRegisterId,
    shouldShowHoldStateComment,
} from './holdStateEventLog';


type SetPageFunction = React.Dispatch<React.SetStateAction<number>>;

interface TableProps {
    data: any[];
    columns: any[];
    defaultSortColumn: string;
    defaultSortOrder: string;
    columnHeaders: any[];
    pagniation?: boolean;
    page: number;
    setPage: SetPageFunction;
    searchValue: string;
    pageSize: number;
    setPageSize: React.Dispatch<React.SetStateAction<number>>
    pageSelected: string;
    setPageSelected: React.Dispatch<React.SetStateAction<string>>
}

export const SortTable: React.FC<TableProps> = ({ data, columns, defaultSortColumn, defaultSortOrder, columnHeaders, pagniation, page, setPage, searchValue, pageSize, setPageSize, pageSelected, setPageSelected }) => {

    const [sortColumn, setSortColumn] = useState(defaultSortColumn);
    const [sortOrder, setSortOrder] = useState(defaultSortOrder);
    const [showDropdown, setShowDropdown] = useState(false);
    const [currentPage, setCurrentPage] = useState(page || 1);
    const [commentTooltip, setCommentTooltip] = useState<{
        open: boolean;
        x: number;
        y: number;
        comment: string;
        source: string;
        date: string;
        loading: boolean;
        activeKey: string;
    }>({ open: false, x: 0, y: 0, comment: "", source: "", date: "", loading: false, activeKey: "" });
    const nonSortableColumns = new Set(['closeDuration', 'arrivalVelocity', 'salesDuration', 'projectedGas']);
    const baseUrl = process.env.REACT_APP_SERVER_ENDPOINT;
    const userPrefrenceApiUrl = `${baseUrl}/UserPreference/UpdatePagePreference`;

    function getBrowserTimeZone() {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    useEffect(() => {
        setCurrentPage(page)
    }, [page])

    useEffect(() => {
        if (!commentTooltip.open) return;

        const handleClickOutside = () => {
            setCommentTooltip((prev) => ({ ...prev, open: false, activeKey: "" }));
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [commentTooltip.open]);

    useEffect(() => {
        if (searchValue === "") {
            setPageSelected(pageSelected)
            setPageSize(pageSize)
        }
    }, [searchValue])

    const handlePageNumberChange = (pageNumber: number) => {
        setPage(pageNumber)
        setCurrentPage(pageNumber);
    }

    // 1) Define a reusable function to call your PUT preferences API
    const updatePageSizePreference = async (newSize: string) => {

        localStorage.setItem("itemsPerPage", newSize);
        try {
            const url = `${userPrefrenceApiUrl}?ItemsPerPage=${newSize}`;

            const response = await fetch(url, {
                method: "PUT", // or "GET" depending on your API contract
                headers: {
                    "Content-Type": "application/json",
                    'Authorization': `Bearer ${localStorage.getItem("jwtAccessToken")}`
                }
            });

            if (!response.ok) {
                throw new Error(`API responded with ${response?.status}`);
            }

            // Optionally read the response body:
            // const result = await response.json();
        } catch (err) {
            console.error("Failed to update page size preference:", err);
            // You could show a toast or set an error state here if desired
        }
    };

    const handlePageSelectedClick = async (event: React.ChangeEvent<HTMLSelectElement>) => {
        // make one api call to save items per page selection
        const selectedValue: string = event.target.value;
        setPageSelected(selectedValue)

        // First, update preference on the server
        await updatePageSizePreference(selectedValue);
        const newSize = selectedValue?.toLowerCase() === "all" ? data?.length + 1 : Number(selectedValue);

        // Calculate new page number to ensure correct positioning after page size change
        const newPage = Math.floor((currentPage - 1) * pageSize / newSize) + 1;



        // Update the page size
        setPageSize(newSize);

        setPage(newPage);

        // Update the current page to the recalculated value
        setCurrentPage(newPage);
    };

    // Sorting logic
    const sortedData = data.sort((a, b) => {
        const sortKey = sortColumn;
        const aValue = a[sortKey];
        const bValue = b[sortKey];

        // Check for null, undefined, or empty strings and move them to the bottom
        const isAEmpty = aValue === null || aValue === undefined || aValue === '';
        const isBEmpty = bValue === null || bValue === undefined || bValue === '';

        if (isAEmpty && !isBEmpty) return 1; // `a` goes below `b`
        if (isBEmpty && !isAEmpty) return -1; // `b` goes below `a`
        if (isAEmpty && isBEmpty) return 0; // Both are empty, keep original order

        if (sortKey === 'alarms') {
            // Special sorting for 'alarms' key based on the count of comma-separated items
            const aCount = aValue ? aValue.split(',').length : 0;
            const bCount = bValue ? bValue.split(',').length : 0;
            return sortOrder === 'asc' ? aCount - bCount : bCount - aCount;
        }

        if (sortKey === 'timeInState') {
            const toSeconds = (time: string) => {
                const [hours = '0', minutes = '0', seconds = '0'] = time.split(':');
                return (+hours * 3600) + (+minutes * 60) + (+seconds);
            };

            const aSeconds = toSeconds(aValue);
            const bSeconds = toSeconds(bValue);

            return sortOrder === 'asc'
                ? aSeconds - bSeconds
                : bSeconds - aSeconds;
        }


        const isANumber = !isNaN(parseFloat(aValue)) && isFinite(aValue);
        const isBNumber = !isNaN(parseFloat(bValue)) && isFinite(bValue);

        if (isANumber && isBNumber) {
            // Numeric sorting
            return sortOrder === 'asc'
                ? parseFloat(aValue) - parseFloat(bValue)
                : parseFloat(bValue) - parseFloat(aValue);
        }

        if (sortKey === 'lastCom') { // Replace 'lastColumnKey' with the actual key for the last column
            const dateA = new Date(aValue);
            const dateB = new Date(bValue);

            // Fallback for invalid dates
            if (isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) return 1;
            if (!isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return -1;
            if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0;

            return sortOrder === 'asc'
                ? dateA.getTime() - dateB.getTime()
                : dateB.getTime() - dateA.getTime();
        }

        if (sortKey === 'version') { // Replace 'version' with the actual key for version column
            const parseVersion = (version: string) => version.split('.').map(Number);

            const aVersion = parseVersion(aValue);
            const bVersion = parseVersion(bValue);

            for (let i = 0; i < Math.max(aVersion.length, bVersion.length); i++) {
                const aPart = aVersion[i] || 0; // Default to 0 if part is missing
                const bPart = bVersion[i] || 0; // Default to 0 if part is missing

                if (aPart !== bPart) {
                    return sortOrder === 'asc' ? aPart - bPart : bPart - aPart;
                }
            }

            return 0; // Versions are equal
        }

        // String sorting
        return sortOrder === 'asc'
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
    });


    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = sortedData.slice(startIndex, endIndex);

    const handleColumnClick = (column: string) => {
        // Check if the column is non-sortable
        if (nonSortableColumns.has(column)) {
            // Do nothing if the column is not sortable
            return;
        }

        // Toggle sorting order if the column is already sorted
        if (column === sortColumn) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            // Set the new column and default to ascending order
            setSortColumn(column);
            setSortOrder('asc');
        }
    };

    const handlePageClick = (page: number) => {
        handlePageNumberChange && handlePageNumberChange(page);
        setShowDropdown(false)
    };

    const handlePreviousPage = () => {
        setShowDropdown(false)
        if (currentPage > 1)
            handlePageNumberChange && handlePageNumberChange(currentPage - 1);
    };

    const handleNextPage = () => {
        setShowDropdown(false)
        const totalPages = Math.ceil(data?.length / pageSize);
        if (currentPage < totalPages)
            handlePageNumberChange && handlePageNumberChange(currentPage + 1);
    };



    const renderPagination = () => {
        const totalPages = Math.ceil(data?.length / pageSize);


        const generatePaginationNumbers = () => {
            const pages = [];
            const maxVisiblePages = 6; // Maximum visible pages excluding ellipses

            if (totalPages <= maxVisiblePages) {
                // If there are 6 or fewer pages, show all pages
                for (let i = 1; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                // Case where there are more than 6 pages

                // If the current page is within the first 4 pages, show first three, ..., and last three
                if (currentPage <= 3) {
                    pages.push(1, 2, 3, '...', totalPages - 2, totalPages - 1, totalPages);
                }
                // If the current page is in the middle, show first page, ..., current page area, ..., last page
                else if (currentPage > 3 && currentPage < totalPages - 2) {
                    pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
                }
                // If the current page is among the last 3 pages, show first three, ..., and the last three pages
                else {
                    pages.push(1, 2, 3, '...', totalPages - 2, totalPages - 1, totalPages);
                }
            }

            return pages;
        };

        const paginationNumbers = generatePaginationNumbers();

        return (
            <div className="flex flex-col md:flex-row items-center justify-between mt-4">
                {/* Left aligned section: Previous button and Rows per page */}
                <div className="flex items-center space-x-2">
                    <button
                        className={`flex items-center text-neutral-25 justify-center rounded-[8px] border-[1px] border-neutral-25 py-[8px] px-[14px] ${currentPage === 1
                            ? "bg-darkcolor-300 cursor-not-allowed"
                            : "bg-transparent text-white"
                            }`}
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                        Previous
                    </button>

                    {/* Rows per page */}
                    <div className="flex items-center space-x-2">
                        <select
                            id="dropdown"
                            className="bg-darkcolor-800 border border-neutral-400 rounded-md text-white"
                            style={{ height: "40px", paddingLeft: "5px", paddingRight: "5px" }}
                            value={pageSelected}
                            onChange={handlePageSelectedClick}
                        >
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                            <option value="ALL">All</option>
                        </select>
                        <h4 className="text-white">Rows per page</h4>
                    </div>
                </div>

                {/* Center aligned Pagination */}
                <div className="flex justify-center relative" style={{ marginRight: "70px" }}>
                    {paginationNumbers.map((page, index) => (
                        <button
                            key={index}
                            className={`mx-1 w-[30px] h-[30px] text-neutral-25 ${currentPage === page ? "rounded-full bg-darkcolor-500" : ""}`}
                            onClick={() => typeof page === 'number' && handlePageClick(page)} // Click only if it's a number
                        >
                            {page}
                        </button>
                    ))}
                </div>

                {/* Right aligned Next button */}
                <button
                    className={`flex items-center text-neutral-25 justify-center rounded-[8px] border-[1px] border-neutral-25 py-[8px] px-[14px] mt-2 md:mt-0 ${currentPage === totalPages
                        ? "bg-darkcolor-300 cursor-not-allowed"
                        : "bg-transparent text-white"
                        }`}
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                >
                    Next
                    <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                </button>
            </div>
        );
    };

    const renderSortIcon = () => {
        return (
            <FontAwesomeIcon
                icon={faSort}
                className={`table-sort-icon ml-1 ${sortOrder === 'asc' ? 'table-sort-icon--asc' : ''}`}
            />
        );
    };

    // const getColumnColorOrHex = (column: string, item: { [key: string]: any }): string => {
    //     if (column === "alarms") {
    //         return item[column] === "OK" ? "green" : "red";
    //     } else if (column === "lastCom") {
    //         return item["hexcode"] || ""; // Return hexcode if it exists
    //     }
    //     return ""; // Default case
    // };
    const getColumnColorOrHex = (column: string, item: { [key: string]: any }): string => {
        if (column === "alarms") {
            const alarmValue = item[column];

            if (alarmValue === "OK") {
                return "green";
            } else {
                return "red"; // Default color for alarms column
            }
        } else if (column === "state") {
            const stateValue = item[column];
            if (stateValue === "Hold Open" || stateValue === "Hold Close") {
                return "red"; // Red for specific state values
            }
            return "white"; // Default white for other state values
        } else if (column === "lastCom") {
            return item["hexcode"] || ""; // Return hexcode if it exists
        }
        return ""; // Default case
    };

    // Add a new function to render colored alarm text for the array of alarms
    const renderAlarmText = (alarmValue: string) => {
        if (alarmValue === "OK") {
            return <span style={{ color: "green" }}>{alarmValue}</span>;
        }

        const whiteAlarms = ["Slow Arrival", "Fast Arrival", "Max Sales Time"];
        const alarmArray = alarmValue ? alarmValue?.split(',')?.map((alarm: string) => alarm?.trim()) : [];

        return (
            <>
                {alarmArray?.map((alarm, index) => {
                    const isWhiteAlarm = whiteAlarms?.some((whiteAlarm) => alarm.includes(whiteAlarm));
                    const color = isWhiteAlarm ? "white" : "red";

                    return (
                        <span key={index}>
                            <span style={{ color }}>{alarm}</span>
                            {index < alarmArray?.length - 1 && <span style={{ color }}>,</span>}
                        </span>
                    );
                })}
            </>
        );
    };

    const handleHoldStateCommentClick = async (
        e: React.MouseEvent<HTMLElement>,
        item: { assetId?: string },
        stateValue: string
    ) => {
        e.stopPropagation();
        const registerState = getHoldStateRegisterId(stateValue);
        if (!registerState || !item.assetId) return;

        const tooltipKey = `${item.assetId}-${registerState}`;
        if (commentTooltip.open && commentTooltip.activeKey === tooltipKey) {
            setCommentTooltip({
                open: false,
                x: 0,
                y: 0,
                comment: "",
                source: "",
                date: "",
                loading: false,
                activeKey: "",
            });
            return;
        }

        const rect = e.currentTarget.getBoundingClientRect();
        setCommentTooltip({
            open: true,
            x: rect.left,
            y: rect.top,
            comment: "",
            source: "",
            date: "",
            loading: true,
            activeKey: tooltipKey,
        });

        try {
            const data = await fetchEventLogDetails(
                item.assetId,
                registerState,
                getBrowserTimeZone()
            );
            setCommentTooltip({
                open: true,
                x: rect.left,
                y: rect.top,
                comment: (data?.comment ?? "").toString(),
                source: (data?.source ?? "").toString(),
                date: (data?.date ?? "").toString(),
                loading: false,
                activeKey: tooltipKey,
            });
        } catch {
            setCommentTooltip({
                open: true,
                x: rect.left,
                y: rect.top,
                comment: "Unable to load comment.",
                source: "",
                date: "",
                loading: false,
                activeKey: tooltipKey,
            });
        }
    };

    function getSeriesShades(series) {
        let shadeColor = "";
        switch (series) {
            case "closeDuration":
                shadeColor = "#FA6020";
                break;
            case "arrivalVelocity":
                shadeColor = "#4294FF";
                break;
            case "projectedGas":
                shadeColor = "#12B76A";
                break;
            case "salesDuration":
                shadeColor = "#FFBF3F";
                break;
            case "target":
                shadeColor = "#828A99";
                break;
            default:
                shadeColor = "#f20d0d";
        }
        return shadeColor;
    }

    return (
        <div className="mx-auto w-full">
            <div className="bg-gray-600">
                <div className="mt-8 flow-root">
                    <div className="overflow-x-auto border border-darkcolor-600 rounded-lg" style={{ backgroundColor: "rgb(37 48 64 / var(--tw-bg-opacity))" }}>
                        <div style={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto", overflowX: "auto" }}>
                            <table className="min-w-full divide-y divide-darkcolor-400">
                                {/* Sticky Header */}
                                <thead className="bg-darkcolor-600">
                                    <tr>
                                        {columns?.filter((column) => column !== "hexcode").map((column, index) => (
                                            <th
                                                scope="col"
                                                style={{
                                                    position: "sticky",
                                                    top: "0", // Fix header on vertical scroll
                                                    left: index === 0 ? "0" : "auto", // Fix first column on horizontal scroll
                                                    zIndex: index === 0 ? 40 : 30, // Higher z-index for first column
                                                    backgroundColor: "var(--Dark-mode-700, #131F2E)", // Ensure background is solid
                                                }}
                                                className={`py-2 pl-2 pr-2 text-left text-sm font-semibold text-white well-compact-column border-r border-darkcolor-400 last:border-r-0
                                ${index === 0 ? "left-0 bg-gray-700 z-10" : ""}
                                ${['closeDuration', 'arrivalVelocity', 'salesDuration', 'projectedGas'].includes(column) ? "sparklines-headers-column" : ""}
                            `}
                                                key={column}
                                                onClick={() => handleColumnClick(column)}
                                            >
                                                <div className="inline-flex items-center gap-1 whitespace-nowrap">
                                                    <span>{columnHeaders[index]}</span>
                                                    {!nonSortableColumns.has(column) && renderSortIcon()}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-darkcolor-400">
                                    {paginatedData?.map((item, index) => {
                                        return (
                                            <tr key={index}>
                                                {columns
                                                    ?.filter((column) => column !== "hexcode")
                                                    ?.map((column, colIndex) => (
                                                        <td
                                                            key={column}
                                                            style={
                                                                colIndex === 0
                                                                    ? {
                                                                        position: "sticky",
                                                                        left: "0",
                                                                        zIndex: 30,
                                                                        backgroundColor: "var(--Dark-mode-700, #131F2E)",
                                                                        whiteSpace: "pre-line",
                                                                        wordBreak: "keep-all",
                                                                    }
                                                                    : {
                                                                        color: column === "alarms" ? undefined : getColumnColorOrHex(column, item),
                                                                        whiteSpace: "pre-line",
                                                                        wordBreak: "keep-all",
                                                                        textAlign:
                                                                            column === "timeInState"
                                                                                ? "right"
                                                                                : undefined,
                                                                    }}
                                                            className={`
                                                                    ${colIndex === 0
                                                                    ? "whitespace-nowrap text-sm text-gray-300 sticky left-0 bg-gray-700 z-10 compact-column"
                                                                    : ['closeDuration', 'arrivalVelocity', 'salesDuration', 'projectedGas'].includes(column)
                                                                        ? "whitespace-nowrap text-sm text-gray-300 sparklines-compact-column"
                                                                        : "whitespace-nowrap px-3 py-4 text-sm text-gray-300 compact-column"
                                                                } ${colIndex !== columns?.length - 1 ? "border-r border-darkcolor-400" : ""}`}
                                                        >
                                                            {colIndex === 0 ? (
                                                                <Link
                                                                    to={`/assetdetails?assetId=${encodeURIComponent(item["assetId"])}`}
                                                                    state={{
                                                                        assetName: item[column],
                                                                        assetId: item["assetId"],
                                                                        customerId: item["customerId"],
                                                                    }}
                                                                    style={{
                                                                        color: "var(--Primary-Indigo-400, #60BFDA)",
                                                                        textDecoration: "none",
                                                                        display: "block",
                                                                    }}
                                                                >
                                                                    {`${item[column]}`}
                                                                </Link>
                                                            ) : ["closeDuration", "arrivalVelocity", "salesDuration", "projectedGas"].includes(column) ? (

                                                                ["projectedGas"].includes(column) ?
                                                                    <Sparklines
                                                                        runTrends={item["runTrends"]}
                                                                        slowArrival={item["slowArrival"]}
                                                                        fastArrival={item["fastArrival"]}
                                                                        column={column}
                                                                        assetid={item["assetId"]}
                                                                    ></Sparklines>
                                                                    :
                                                                    <SparklineChart
                                                                        data={[...item["runTrends"]?.map((trend: any) => trend[column])].reverse()}
                                                                        color={getSeriesShades(column)}
                                                                        type={column}
                                                                        maximum={2 * item["fastArrival"]}
                                                                        fastArrival={item["fastArrival"]}
                                                                        slowArrival={item["slowArrival"]}
                                                                        projectedGasTarget={[...item["runTrends"]?.map((trend: any) => trend?.target)].reverse()}


                                                                    />
                                                            ) : column === "alarms" ? (
                                                                renderAlarmText(item[column])
                                                            ) : column === "state" ? (
                                                                (() => {
                                                                    const stateValue = item[column];
                                                                    if (!shouldShowHoldStateComment(stateValue)) return stateValue;

                                                                    return (
                                                                        <div className="relative inline-flex items-center gap-2">
                                                                            <span>{stateValue}</span>
                                                                            <span
                                                                                aria-label="View comment"
                                                                                className="inline-flex items-center justify-center w-5 h-5 text-primary-400 cursor-pointer"
                                                                                onClick={(e) => handleHoldStateCommentClick(e, item, stateValue)}
                                                                            >
                                                                                <svg
                                                                                    width="18"
                                                                                    height="18"
                                                                                    viewBox="0 0 24 24"
                                                                                    fill="none"
                                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                                >
                                                                                    <path
                                                                                        d="M21 12c0 4.418-4.03 8-9 8-1.02 0-2.003-.148-2.93-.423L3 21l1.565-4.01A7.92 7.92 0 0 1 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8Z"
                                                                                        stroke="currentColor"
                                                                                        strokeWidth="1.6"
                                                                                        strokeLinejoin="round"
                                                                                    />
                                                                                </svg>
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                })()
                                                            ) : (
                                                                item[column]
                                                            )
                                                            }
                                                        </td>
                                                    ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>

                        </div>
                    </div>
                </div>
            </div>
            {pagniation && renderPagination()}

            {commentTooltip.open && (
                <div
                    className="fixed z-[9999]"
                    style={{
                        left: commentTooltip.x + 28,
                        top: commentTooltip.y + 10,
                        transform: "translateY(-50%)",
                        minWidth: "320px",
                        maxWidth: "520px",
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="bg-darkcolor-700 border border-darkcolor-500 rounded-xl shadow-lg p-4">
                        {commentTooltip.loading ? (
                            <div className="text-neutral-200 text-[14px]">Loading…</div>
                        ) : (
                            <>
                                <div className="text-white text-[16px] font-medium break-words min-h-[24px]">
                                    {commentTooltip.comment}
                                </div>
                                <div className="mt-2 text-neutral-200 text-[13px] min-h-[18px] break-words">
                                    {commentTooltip.source}
                                </div>
                                <div className="mt-1 text-neutral-300 text-[13px] min-h-[18px] break-words">
                                    {commentTooltip.date}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>

    );
};
